import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Brain,
  Shield,
  LineChart,
  Target,
  Sparkles,
  Clock,
  BadgeCheck,
  Users,
} from "lucide-react";

const BENEFITS = [
  { icon: Brain, label: "Psychology Score" },
  { icon: BadgeCheck, label: "Discipline Score" },
  { icon: Shield, label: "Risk Management Score" },
  { icon: LineChart, label: "Strategy Score" },
  { icon: Target, label: "Personalized Recommendations" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.05, ease: "easeOut" },
  }),
};

export default function Landing() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gradient-radial pointer-events-none" />
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />

        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 pt-16 sm:pt-24 pb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-background/60 backdrop-blur text-xs font-medium text-muted-foreground"
          >
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            Built by traders, for traders
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-display mt-6 text-4xl sm:text-6xl font-semibold tracking-tighter leading-[1.05]"
          >
            Find Out Why You&rsquo;re
            <br />
            <span className="text-primary">Not Profitable.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed"
          >
            Get a personalized <strong className="text-foreground">AI Trading Health Report</strong>{" "}
            in just 3 minutes. No signup. No fluff. Just the truth about your trading behaviour.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <Link
              to="/assessment"
              data-testid="hero-start-assessment-btn"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium shadow-[0_8px_24px_rgba(0,82,255,0.25)] hover:shadow-[0_12px_28px_rgba(0,82,255,0.32)] hover:-translate-y-[1px] transition-all"
            >
              Start Free Assessment
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <span className="text-xs text-muted-foreground">
              Takes 3 minutes · 100% free · No signup
            </span>
          </motion.div>

          {/* Benefits list */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-12 grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-4xl mx-auto"
          >
            {BENEFITS.map((b) => (
              <div
                key={b.label}
                className="flex flex-col items-start gap-2 p-4 rounded-xl border border-border bg-card soft-shadow"
                data-testid={`benefit-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                  <b.icon className="w-4 h-4 text-primary" />
                </div>
                <p className="text-xs sm:text-sm font-medium text-left leading-snug">
                  {b.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats section */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={LineChart}
            value="10,000+"
            label="Trades Analysed"
            testId="stat-trades-analysed"
          />
          <StatCard
            icon={Clock}
            value="3 min"
            label="Assessment Time"
            testId="stat-time"
          />
          <StatCard
            icon={Users}
            value="100%"
            label="Free Forever"
            testId="stat-free"
          />
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-24">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            How it works
          </p>
          <h2 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">
            Three minutes. Five scores. One clear path.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              n: "01",
              t: "Answer 5 quick sections",
              d: "Your profile, performance, psychology & goals. Every question has a purpose.",
            },
            {
              n: "02",
              t: "We analyse silently",
              d: "A rule-based engine benchmarks you across 4 dimensions and 10 psychology traits.",
            },
            {
              n: "03",
              t: "See exactly what's broken",
              d: "Get scores, strengths, weaknesses & a prioritized action list you can start today.",
            },
          ].map((s) => (
            <div
              key={s.n}
              className="p-6 rounded-xl border border-border bg-card soft-shadow"
            >
              <p className="font-display text-3xl font-semibold text-primary tabular-nums">
                {s.n}
              </p>
              <p className="mt-3 font-medium text-lg">{s.t}</p>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                {s.d}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-transparent to-transparent p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Stop guessing. Start improving.
            </h3>
            <p className="mt-2 text-muted-foreground max-w-xl">
              A better trader starts with a better mirror. Get yours in 3 minutes.
            </p>
          </div>
          <Link
            to="/assessment"
            data-testid="cta-bottom-start-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:-translate-y-[1px] transition-all"
          >
            Start Free Assessment
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, value, label, testId }) {
  return (
    <div
      data-testid={testId}
      className="p-6 rounded-xl border border-border bg-card soft-shadow flex items-center gap-4"
    >
      <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <p className="font-display text-3xl font-semibold tracking-tighter tabular-nums">
          {value}
        </p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}
